import marketService from '@/services/amio/market.service'
import marketModule from '@/store/modules/market.module'
import botModule from '@/store/modules/bot.module'
import { VARIABLE_SCOPE } from '@/store/constants'

export const MARKET_MODE = {
  EXISTING: 'existing',
  NEW: 'new'
}

export function createDefaultMarketForm(defaults = {}) {
  return {
    mode: MARKET_MODE.EXISTING,
    sourceMarketId: null,
    name: '',
    language: defaults.language || 'en',
    timezone: defaults.timezone || 'Europe/Prague'
  }
}

function getMarketIdsUsedInMultiBotGroup(multiBots = [], multiBotGroupId = null) {
  if (!multiBotGroupId) return new Set()

  const marketIds = new Set()
  multiBots.forEach(multiBot => {
    if (multiBot.primaryBot?.multi_bot_id !== multiBotGroupId) return
    const marketId = multiBot.primaryBot?.market_id
    if (marketId) marketIds.add(marketId)
  })
  return marketIds
}

function normalizePrimaryBotId(multiBot) {
  return multiBot?.primary_bot_id || multiBot?.primaryBotId || null
}

function resolveMarketList(markets) {
  return Array.isArray(markets) ? markets : marketModule.getMarketList()
}

export function getMarketsForMultiBotGroup(multiBotGroupId, markets = null) {
  if (!multiBotGroupId) return []

  const marketList = resolveMarketList(markets)
  const bots = botModule.getBotList()
  const multiBots = botModule.getBotVariantList()
  const primaryBot = bots.find(bot => bot.multi_bot_id === multiBotGroupId && bot.is_primary_variant) ||
    bots.find(bot => bot.multi_bot_id === multiBotGroupId)

  if (!primaryBot) return []

  const siblingBots = bots.filter(bot =>
    bot.id === primaryBot.id || bot.multi_bot_id === multiBotGroupId
  )
  const siblingBotIds = new Set(siblingBots.map(bot => bot.id))

  return multiBots
    .filter(multiBot => {
      const marketPrimaryBotId = normalizePrimaryBotId(multiBot)
      return marketPrimaryBotId && siblingBotIds.has(marketPrimaryBotId)
    })
    .map(multiBot => {
      const marketId = multiBot.primaryBot?.market_id
      if (!marketId) return null

      const market = marketList.find(item => item.id === marketId)
      return market || { id: marketId, name: multiBot.name || marketId }
    })
    .filter(Boolean)
}

export function getMarketsForCurrentBot() {
  const bot = botModule.getBot()
  return bot?.multi_bot_id ? getMarketsForMultiBotGroup(bot.multi_bot_id) : []
}

// Expands selected logical bots (standalone bot ids or multi-bot group ids) into real bot ids.
// marketIds: null = all markets; [] = none (multimarket groups contribute nothing).
// The market filter never applies to standalone bots — they are included whenever checked.
export function getBotIdsForLogicalBots(logicalBotIds = [], marketIds = null) {
  const bots = botModule.getBotList()
  const selectedMarkets = marketIds === null ? null : new Set(marketIds)

  return (logicalBotIds || []).flatMap(logicalBotId => {
    const variantBots = bots.filter(({ multi_bot_id }) => multi_bot_id === logicalBotId)
    if (!variantBots.length) {
      const standaloneBot = bots.find(({ id }) => id === logicalBotId)
      return standaloneBot ? [standaloneBot.id] : []
    }
    return variantBots
      .filter(({ market_id }) => selectedMarkets === null || selectedMarkets.has(market_id))
      .map(({ id }) => id)
  })
}

// Union of markets across the selected logical bots (standalone ids contribute nothing).
export function getMarketsForLogicalBots(logicalBotIds = []) {
  const seenMarketIds = new Set()
  return (logicalBotIds || [])
    .flatMap(logicalBotId => getMarketsForMultiBotGroup(logicalBotId))
    .filter(({ id }) => {
      if (seenMarketIds.has(id)) return false

      seenMarketIds.add(id)
      return true
    })
}

// The logical bot id a real bot belongs to (its multi-bot group id, or its own id).
export function getLogicalBotId(bot) {
  return bot?.multi_bot_id || bot?.id || null
}

export function getAvailableMarketOptions(multiBots = [], multiBotGroupId = null) {
  const marketIdsInCurrentGroup = getMarketIdsUsedInMultiBotGroup(multiBots, multiBotGroupId)
  return marketModule.getMarketList()
    .filter(market => !marketIdsInCurrentGroup.has(market.id))
    .map(market => ({
      value: market.id,
      text: market.name
    }))
}

export function marketHasNoTimezone(marketId, bots = []) {
  if (!marketId) return false
  return !bots.some(bot => bot.market_id === marketId)
}

export function isMarketFormValid(form, hasExistingMarketOptions, bots = []) {
  if (hasExistingMarketOptions && form.mode === MARKET_MODE.EXISTING) {
    if (!form.sourceMarketId) return false
    if (marketHasNoTimezone(form.sourceMarketId, bots)) return !!form.timezone
    return true
  }
  return !!(form.name?.trim() && form.language && form.timezone)
}

export function getMarketFormLanguageTimezone(form) {
  if (form.mode === MARKET_MODE.EXISTING && form.sourceMarketId) {
    return {
      language: marketModule.getMarketList().find(market => market.id === form.sourceMarketId)?.language || form.language,
      timezone: botModule.getBotList().find(bot => bot.market_id === form.sourceMarketId)?.timezone || form.timezone
    }
  }

  return {
    language: form.language,
    timezone: form.timezone
  }
}

export function getLogicalBotOptions() {
  const bots = botModule.getBotList()
  const options = []
  const seenGroups = new Set()

  bots.forEach(bot => {
    if (!bot.multi_bot_id) {
      options.push({ value: bot.id, text: bot.name, isMulti: false })
      return
    }
    if (seenGroups.has(bot.multi_bot_id)) return
    seenGroups.add(bot.multi_bot_id)
    const primaryBot = bots.find(b => b.id === bot.primary_bot_id) || bot
    options.push({ value: bot.multi_bot_id, text: primaryBot.name, isMulti: true })
  })

  return options
}

// Convert a scope-picker selection -> API column payload { bot_id, multi_bot_id }.
// A variable targets exactly one column (or none = global). "bot+market" is the variant bot.
export function variableScopePayload(selection = {}) {
  if (selection.scope === VARIABLE_SCOPE.BOT) {
    return selection.is_multi ?
      { bot_id: null, multi_bot_id: selection.scope_value || null } :
      { bot_id: selection.scope_value || null, multi_bot_id: null }
  }
  if (selection.scope === VARIABLE_SCOPE.BOT_MARKET) {
    return { bot_id: resolveVariantBotId(selection.scope_value, selection.market_id), multi_bot_id: null }
  }
  return { bot_id: null, multi_bot_id: null }
}

// Convert a variable's API columns -> scope-picker selection.
export function variableScopeSelection(variable = {}) {
  if (variable.multi_bot_id) {
    return { scope: VARIABLE_SCOPE.BOT, scope_value: variable.multi_bot_id, is_multi: true }
  }
  if (variable.bot_id) {
    const bot = botModule.getBotList().find(b => b.id === variable.bot_id)
    if (bot && bot.multi_bot_id) {
      return { scope: VARIABLE_SCOPE.BOT_MARKET, scope_value: bot.multi_bot_id, market_id: bot.market_id }
    }
    return { scope: VARIABLE_SCOPE.BOT, scope_value: variable.bot_id, is_multi: false }
  }
  return { scope: VARIABLE_SCOPE.GLOBAL }
}

// Derive the display level of a variable from its columns.
export function variableScopeLevel(variable = {}) {
  if (variable.multi_bot_id) return VARIABLE_SCOPE.BOT
  if (variable.bot_id) {
    const bot = botModule.getBotList().find(b => b.id === variable.bot_id)
    return (bot && bot.multi_bot_id) ? VARIABLE_SCOPE.BOT_MARKET : VARIABLE_SCOPE.BOT
  }
  return VARIABLE_SCOPE.GLOBAL
}

// The real market name for a bot variant. Never use `bot.name`/`bot.multi_bot_name` directly for
// this — they're only seeded identically to the market's name at creation time and can silently
// drift once either is renamed independently. Falls back to the variant's own name only if the
// market row itself can't be found (e.g. the market list hasn't loaded yet).
export function marketNameForBot(bot) {
  const market = marketModule.getMarketList().find(m => m.id === bot.market_id)
  return market ? market.name : (bot.multi_bot_name || bot.multiBotName || bot.name)
}

// The shared multibot/group name for a bot variant — its primary bot's own name, never the
// variant's own name (which is a market label, not the group's display name).
export function multiBotNameForBot(bot) {
  return botModule.resolvePrimaryBot(bot.id)?.name || bot.name
}

export function resolveVariantBotId(multiBotGroupId, marketId) {
  const bot = botModule.getBotList()
    .find(b => b.multi_bot_id === multiBotGroupId && b.market_id === marketId)
  return bot ? bot.id : null
}

// Resolves the bot id for opening an answer editor link. When exactly one market
// is selected on a multi-market bot, returns that market's variant bot id.
export function resolveAnswerRouteBotId(botId, marketIds = null) {
  if (!botId || !marketIds || marketIds.length !== 1) return botId

  const bot = botModule.getBotList().find(b => b.id === botId)
  if (!bot?.multi_bot_id) return botId

  return resolveVariantBotId(bot.multi_bot_id, marketIds[0]) || botId
}

// Resolves a logical bot id (a standalone bot id, or a multi-bot group id) to a real,
// routable bot id for /settings/bots/:botId. For a group it returns the primary variant.
export function resolveBotRouteId(logicalBotId) {
  const bots = botModule.getBotList()
  const standalone = bots.find(b => b.id === logicalBotId && !b.multi_bot_id)
  if (standalone) return standalone.id

  const variants = bots.filter(b => b.multi_bot_id === logicalBotId)
  if (!variants.length) return null
  const primary = variants.find(b => b.id === b.primary_bot_id) || variants[0]
  return primary.id
}

// Groups markets by the multi-bot they belong to, for the hierarchical "Market" scope list.
// Each group: { multiBotId, botName, markets: [{ value, text }] }. Groups with no markets are omitted.
export function getMarketScopeGroups() {
  const bots = botModule.getBotList()
  const markets = marketModule.getMarketList()
  const groupsById = new Map()

  bots.forEach(bot => {
    if (!bot.multi_bot_id) return
    if (!groupsById.has(bot.multi_bot_id)) {
      const primaryBot = bots.find(b => b.id === bot.primary_bot_id) || bot
      groupsById.set(bot.multi_bot_id, { multiBotId: bot.multi_bot_id, botName: primaryBot.name, markets: [] })
    }
    if (!bot.market_id) return
    const market = markets.find(m => m.id === bot.market_id)
    if (market) groupsById.get(bot.multi_bot_id).markets.push({ value: market.id, text: market.name })
  })

  return [...groupsById.values()].filter(group => group.markets.length > 0)
}

export async function resolveMarketId(form) {
  if (form.mode === MARKET_MODE.EXISTING && form.sourceMarketId) {
    const market = marketModule.getMarketList().find(market => market.id === form.sourceMarketId)
    return {
      marketId: form.sourceMarketId,
      marketName: market.name,
      timezone: botModule.getBotList().find(bot => bot.market_id === market.id)?.timezone || form.timezone,
      language: market.language,
      createdMarket: false
    }
  }

  const newMarketName = `${form.name?.trim()} - ${form.language.toUpperCase()}`
  const createdMarket = await marketService.create(form.language, newMarketName)
  return {
    marketId: createdMarket.id,
    marketName: newMarketName,
    timezone: form.timezone,
    language: form.language,
    createdMarket: true
  }
}
